import { useTenantVat } from '@hooks';
import { OrderStatus } from '@models';
import { Service } from '@services';
import { IServiceBasket, ServiceBasket, useStore } from '@stores';
import { useCallback, useRef, useState } from 'react';

/** One unsaved job, with its own service basket. */
export interface DraftJob {
  /** Stable across re-renders and independent of the services in it, which the user can change. */
  key: string;
  basket: IServiceBasket;
  /**
   * This job's own status. Per job rather than per build, so one leg can go out as an estimate
   * (Approval) while the rest are booked in as Booked.
   */
  status: string;
  /**
   * When this stage starts. Undefined means "use the build's drop-off" - the legs of a restoration
   * are often weeks apart, but the common case is that they all start together.
   */
  dropOffDate?: string;
  /** Technician the job is assigned to. Undefined leaves it unassigned, as an ordinary booking. */
  userId?: number;
  /**
   * The calendar slot, when the user gives the leg one here. Left undefined the job is created
   * unassigned and placed on the calendar later, which is how a build was booked before.
   */
  startAt?: Date | string;
  endAt?: Date | string;
}

interface Options {
  /**
   * Whether the last remaining job may be removed. The booking drawer says no - a build cannot be
   * created with nothing in it - while the build page says yes, since the build already exists and
   * the draft is simply abandoned.
   */
  allowEmpty?: boolean;
}

/**
 * A list of unsaved jobs, each with its own service basket.
 *
 * Baskets rather than serviceStore because several jobs are being edited at once and none of them
 * is saved yet; serviceStore holds a single basket, which the saved-order screens keep using.
 * Shared by the booking drawer, which drafts a whole build, and the build page, which appends one
 * job to a build that already exists.
 */
export const useDraftJobs = ({ allowEmpty = false }: Options = {}) => {
  const { serviceStore } = useStore();
  const vat = useTenantVat();

  const [jobs, setJobs] = useState<DraftJob[]>([]);
  const [activeKey, setActiveKey] = useState<string | undefined>();

  // Keys only have to be unique within one drafting session, so a counter beats anything derived
  // from the services, which the user is free to change.
  const nextKey = useRef(0);
  const makeKey = () => {
    nextKey.current += 1;
    return `job-${nextKey.current}`;
  };

  const newBasket = useCallback(
    () => new ServiceBasket(serviceStore.selectServices, vat),
    [serviceStore, vat]
  );

  const addJob = useCallback(
    (dropOffDate?: string) => {
      const job = { key: makeKey(), basket: newBasket(), status: OrderStatus.Booked, dropOffDate };
      setJobs((current) => [...current, job]);
      setActiveKey(job.key);
      return job.key;
    },
    [newBasket]
  );

  /** Replaces the whole list, one job per service - how a template is expanded. */
  const setJobsFromServices = useCallback(
    (services: Service[]) => {
      const expanded = services.map((service) => {
        const basket = new ServiceBasket(serviceStore.selectServices, vat);
        basket.seedFromServices([service]);
        return { key: makeKey(), basket, status: OrderStatus.Booked };
      });
      setJobs(expanded);
      setActiveKey(expanded[0]?.key);
    },
    [serviceStore, vat]
  );

  /**
   * One setter for every editable field on a draft. A named setter per field meant five almost
   * identical functions and five props threaded down to the same panel.
   */
  const updateJob = useCallback((key: string, patch: Partial<DraftJob>) => {
    setJobs((current) => current.map((job) => (job.key === key ? { ...job, ...patch } : job)));
  }, []);

  const removeJob = useCallback(
    (key: string) => {
      setJobs((current) => {
        if (!allowEmpty && current.length <= 1) return current;
        const next = current.filter((job) => job.key !== key);
        setActiveKey((active) => (active === key ? next[0]?.key : active));
        return next;
      });
    },
    [allowEmpty]
  );

  const clearJobs = useCallback(() => {
    setJobs([]);
    setActiveKey(undefined);
  }, []);

  /** A job with no services on it is not something anyone can save. */
  const hasUsableJob = jobs.some((job) => job.basket.selectedServices.length > 0);

  return {
    jobs,
    activeKey,
    setActiveKey,
    addJob,
    removeJob,
    clearJobs,
    setJobsFromServices,
    updateJob,
    hasUsableJob,
  };
};
